import { useEffect, useState } from "react";
import { Segmented } from "antd";
import SectionPanel from "./SectionPanel";
import {
  ChartSkeleton,
  EmptyMessage,
  ErrorBanner,
  TableSkeleton,
} from "./AnalyticsStates";
import useAttributionDashboard from "hooks/useAttributionDashboard";
import AttributionFilters from "pages/analytics/traffic-attribution/components/AttributionFilters";
import AttributionInsightsPanel from "pages/analytics/traffic-attribution/components/AttributionInsightsPanel";
import AttributionDrilldownTable from "pages/analytics/traffic-attribution/components/AttributionDrilldownTable";
import AttributionDrilldownChart from "pages/analytics/traffic-attribution/components/AttributionDrilldownChart";

const FILTER_KEY = "attribution_drilldown_filters";
const EMPTY_FILTERS = { platform: null, account: null, campaign: null };

export default function AttributionDashboard({
  api,
  filters: dateFilters,
  showChart = false,
  embedded = false,
}) {
  const [view, setView] = useState("table");
  const [drilldownFilters, setDrilldownFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    const saved = sessionStorage.getItem(FILTER_KEY);
    if (!saved) return;
    try {
      setDrilldownFilters({ ...EMPTY_FILTERS, ...JSON.parse(saved) });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem(FILTER_KEY, JSON.stringify(drilldownFilters));
  }, [drilldownFilters]);

  const { rows, filterOptions, insights, isLoading, error, isRangeIncomplete } =
    useAttributionDashboard(api, dateFilters, drilldownFilters);

  const viewToggle = showChart ? (
    <Segmented
      size="small"
      value={view}
      onChange={setView}
      options={[
        { label: "Table", value: "table" },
        { label: "Chart", value: "chart" },
      ]}
    />
  ) : null;

  return (
    <section className={embedded ? "" : "mb-8"}>
      <AttributionInsightsPanel insights={insights} isLoading={isLoading} />

      <SectionPanel title="Attribution Drilldown" action={viewToggle}>
        <div className="mb-4">
          <AttributionFilters
            filters={drilldownFilters}
            options={filterOptions}
            onChange={setDrilldownFilters}
            onReset={() => setDrilldownFilters(EMPTY_FILTERS)}
          />
        </div>

        {error && (
          <div className="mb-4">
            <ErrorBanner message={error} />
          </div>
        )}

        {isLoading ? (
          view === "table" || !showChart ? (
            <TableSkeleton rows={8} cols={7} />
          ) : (
            <ChartSkeleton height={320} />
          )
        ) : isRangeIncomplete ? (
          <EmptyMessage message="Select start and end dates to view attribution drilldown." />
        ) : error ? (
          <EmptyMessage message="Attribution drilldown unavailable due to a loading error." />
        ) : rows.length === 0 ? (
          <EmptyMessage message="No attribution data for the selected filters." />
        ) : view === "table" || !showChart ? (
          <AttributionDrilldownTable rows={rows} />
        ) : (
          <AttributionDrilldownChart rows={rows} />
        )}
      </SectionPanel>
    </section>
  );
}
